import { Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { RoleChip } from "@/components/shared/RoleChip";
import type { CertificationDefinitionDto } from "@/api/certifications";
import { formatGate } from "./gateFormat";
import { formatDateInTz, useDateFormatter } from "@/utils/datetime";

interface Props {
  rows: CertificationDefinitionDto[];
  loading: boolean;
  error: string | null;
  emptyMessage?: string;
  canUpdate: boolean;
  canDeactivate: boolean;
  onEdit: (row: CertificationDefinitionDto) => void;
  onDeactivate: (row: CertificationDefinitionDto) => void;
  onReactivate: (row: CertificationDefinitionDto) => void;
}

function relative(date: string, tz: string): string {
  const dt = new Date(date);
  const diffMs = Date.now() - dt.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateInTz(date, tz);
}

function RolesCell({ codes }: { codes: string[] }) {
  if (codes.length === 0) {
    return <span className="text-small text-gray-mid">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-space-1">
      {codes.map((c) => <RoleChip key={c} code={c} />)}
    </div>
  );
}

export function CertificationDefinitionsTable({
  rows, loading, error, emptyMessage,
  canUpdate, canDeactivate,
  onEdit, onDeactivate, onReactivate,
}: Props) {
  const { dateTime, tz } = useDateFormatter();
  const columns: Column<CertificationDefinitionDto>[] = [
    {
      key: "entityType",
      header: "Entity",
      render: (r) => (
        <span className={r.active ? undefined : "opacity-60"}>
          <span className="font-mono text-small font-semibold text-onyx">{r.entityType}</span>
        </span>
      ),
    },
    {
      key: "gate",
      header: "Gate",
      render: (r) => (
        <span className={r.active ? "text-small text-onyx" : "text-small text-gray-mid"}>
          {formatGate(r.stageFrom, r.stageTo)}
        </span>
      ),
    },
    {
      key: "label",
      header: "Label",
      render: (r) => (
        <div className={r.active ? undefined : "opacity-60"}>
          <div className="font-semibold text-onyx">{r.label}</div>
          {r.description && (
            <div className="text-caption text-gray-mid leading-tight" title={r.description}>
              {r.description.length > 80 ? `${r.description.slice(0, 80)}…` : r.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "requiredRoles",
      header: "Required roles",
      render: (r) => <RolesCell codes={r.requiredRoles} />,
    },
    {
      key: "revokeRoles",
      header: "Revoke roles",
      render: (r) => <RolesCell codes={r.revokeRoles} />,
    },
    {
      key: "sortOrder",
      header: "Order",
      render: (r) => (
        <span className="text-small text-onyx tabular-nums">{r.sortOrder}</span>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (r) => (
        <span
          className={
            r.active
              ? "text-label text-status-success uppercase tracking-wide"
              : "text-label text-gray-mid uppercase tracking-wide"
          }
        >
          {r.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "modified",
      header: "Last modified",
      render: (r) => {
        const ts = r.modifiedAt ?? r.createdAt;
        return (
          <span
            className="text-small text-gray-mid"
            title={dateTime(ts)}
          >
            {relative(ts, tz)}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex items-center gap-space-2 justify-end">
          {canUpdate && r.active && (
            <Button size="sm" variant="tertiary" type="button" onClick={() => onEdit(r)}>
              Edit
            </Button>
          )}
          {canDeactivate && (r.active ? (
            <Button size="sm" variant="tertiary" type="button" onClick={() => onDeactivate(r)}>
              Deactivate
            </Button>
          ) : (
            <Button size="sm" variant="tertiary" type="button" onClick={() => onReactivate(r)}>
              Reactivate
            </Button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={loading}
      error={error}
      emptyMessage={emptyMessage ?? "No certification definitions match the current filters."}
      rowKey={(r) => r.id}
    />
  );
}
